<script lang="ts">
import axios, { AxiosError } from 'axios';
import i18n from '@/localisation/i18n';
import { LoginSalt } from '@/utils/helpers/rest';
import { errorString, i18nServerMessage } from '@/utils/helpers/rest-response';
import { setInputFocus } from '@/utils/helpers/input-form';
import { SymmetricEncrypter } from '@/modules/common/services/crypto/symmetric-encrypter';
import Password, { PasswordScore } from 'vue-password-strength-meter';
import { computed, onBeforeMount, shallowRef } from 'vue';
import { useEncrypterService } from '@/modules/common/services/encrypter-service';
import { useRouter } from '@/router/router';
import { store as vuex } from '@/store/store';
import { VTextField } from 'vuetify/lib/components';
import { useRules } from '@/composables/vuetify-validation';

const MIN_PASSWORD_SCORE: PasswordScore = 3;
</script>

<script setup lang="ts">
const router = useRouter();
const encrypterService = useEncrypterService();

const props = withDefaults(defineProps<{ secretCode?: string }>(), { secretCode: '' });

const clientConfig = computed(() => vuex.state.clientConfig!);

const symmetricEncrypter = shallowRef<null | SymmetricEncrypter>(null);

const cmpLoginPassword = shallowRef<null | typeof VTextField>(null);
const cmpLoginPasswordAgain = shallowRef<null | typeof VTextField>(null);

const emailAddress = shallowRef('');
const loginPassword = shallowRef('');
const loginPasswordAgain = shallowRef('');
const isProcessing = shallowRef(false);
const revealPassword = shallowRef(false);
const revealPasswordAgain = shallowRef(false);
const passwordScore = shallowRef(0);
const freshAccount = shallowRef(false);
const serviceErrors = shallowRef('');

const canSubmit = computed(() => passwordScore.value >= MIN_PASSWORD_SCORE);

const passwordFeedback = shallowRef<{ warning: string; suggestions: string[] }>({
  warning: '',
  suggestions: [],
});
const messageType = computed(() => (passwordFeedback.value?.warning ? 'error' : 'info'));

const { rules, isSubmitted, validateAndSubmit } = useRules<{
  loginPassword: string;
  loginPasswordAgain: string;
}>({
  loginPassword: {
    rules: [
      () => ((serviceErrors.value = ''), true),
      (v) => !!v || `${i18n.t('loginPassword')} ${i18n.t('formfield.IsRequired')}`,
      // password strength check is a responsibility of the client
      // the server only sees the encrypted password
      () =>
        !isSubmitted.value ||
        passwordScore.value >= MIN_PASSWORD_SCORE ||
        i18n.t('errPasswordTooSimple').toString(),
    ],
  },
  loginPasswordAgain: {
    rules: [
      () => ((serviceErrors.value = ''), true),
      (v) => !!v || `${i18n.t('loginPasswordAgain')} ${i18n.t('formfield.IsRequired')}`,
      (v) =>
        !isSubmitted.value ||
        v === loginPassword.value ||
        i18n.t('errPasswordsNotTheSame').toString(),
    ],
  },
});

onBeforeMount(async () => {
  symmetricEncrypter.value = encrypterService.getSymmetricEncrypter();

  try {
    const data = await vuex.dispatch('prepareNewLoginPassword', props.secretCode);

    emailAddress.value = data.email;
    freshAccount.value = data.freshAccount;

    setInputFocus(cmpLoginPassword.value);
  } catch (e) {
    await router.push({
      name: 'lost-password',
      params: { lostEmail: emailAddress.value, lostMessage: errorString(e) },
    });
  }
});

const onSubmit = () =>
  validateAndSubmit(async () => {
    if (isProcessing.value) return;
    isProcessing.value = true;

    try {
      // password strength check is a responsibility of the client
      // the server only sees the encrypted password
      if (passwordScore.value < MIN_PASSWORD_SCORE) {
        serviceErrors.value = i18n.tc('errPasswordTooSimple');
        setInputFocus(cmpLoginPassword.value);
        return;
      }
      if (loginPassword.value !== loginPasswordAgain.value) {
        serviceErrors.value = i18n.tc('errPasswordsNotTheSame');
        setInputFocus(cmpLoginPasswordAgain.value);
        return;
      }

      // get salt (for entered email address) from the server
      let salt: Buffer;
      try {
        const response = await axios.get(`/api/1/login-salt/${emailAddress.value}`);
        salt = Buffer.from((response.data as LoginSalt).salt, 'base64');
      } catch {
        setInputFocus(cmpLoginPassword.value);
        serviceErrors.value = i18n.tc('rest.ServiceUnavailable');
        return;
      }

      // Send 1 password-hash to the server
      const hashedPassword = await symmetricEncrypter.value?.hashPassword(
        loginPassword.value,
        salt
      );
      try {
        await axios.post('/api/1/login-set-password', {
          encSecret: props.secretCode,
          newPassword: hashedPassword,
        });
      } catch (e) {
        let err = i18n.t('loginResetPasswordEmailFailed', {
          emailAddress: emailAddress.value,
        }) as string;
        const data = (e as AxiosError).response?.data as undefined | { msgkey: unknown };
        if (data?.msgkey) {
          err = i18nServerMessage(e as AxiosError);
        }
        serviceErrors.value = err;
        setInputFocus(cmpLoginPassword.value);
        return;
      }

      await router.push({
        name: 'login',
        params: { email: emailAddress.value, message: 'loginResetPasswordEmailSucceeded' },
      });
    } finally {
      isProcessing.value = false;
    }
  });
</script>

<template>
  <VDialog
    content-class="au-popup-dialog"
    max-width="700px"
    no-click-animation
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistent
    :value="true"
  >
    <VForm @submit.prevent="onSubmit">
      <VCard>
        <VCardTitle>
          <span class="headline">{{
            freshAccount
              ? $t('setPasswordFreshAccountTitle', { title: clientConfig?.systemTitleShort })
              : $t('setPasswordTitle', { title: clientConfig?.systemTitleShort })
          }}</span>
        </VCardTitle>
        <VCardText>
          <VContainer>
            <VRow>
              <VCol cols="12">
                <VTextField
                  v-model="emailAddress"
                  autocomplete="new-password"
                  disabled
                  :label="$t('loginEmail')"
                  required
                  type="email"
                ></VTextField>
              </VCol>
              <VCol cols="12">
                <VTextField
                  :ref="(r) => (cmpLoginPassword = r)"
                  v-model="loginPassword"
                  :append-icon="revealPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  autocomplete="new-password"
                  counter
                  :label="$t('loginPassword')"
                  required
                  :type="revealPassword ? 'text' : 'password'"
                  v-bind="rules.loginPassword"
                  @click:append="revealPassword = !revealPassword"
                ></VTextField>
              </VCol>
              <VCol cols="12">
                <VTextField
                  :ref="(r) => (cmpLoginPasswordAgain = r)"
                  v-model="loginPasswordAgain"
                  :append-icon="revealPasswordAgain ? 'mdi-eye' : 'mdi-eye-off'"
                  autocomplete="new-password"
                  counter
                  :label="$t('loginPasswordAgain')"
                  required
                  :type="revealPasswordAgain ? 'text' : 'password'"
                  v-bind="rules.loginPasswordAgain"
                  @click:append="revealPasswordAgain = !revealPasswordAgain"
                >
                </VTextField>
              </VCol>
              <VCol cols="12">
                <label>Password strength</label>
                <Password
                  v-model="loginPassword"
                  :strength-meter-only="true"
                  @feedback="(feedback) => (passwordFeedback = feedback)"
                  @score="(score) => (passwordScore = score)"
                />
              </VCol>
              <VCol cols="12">
                <div class="alert-wrapper">
                  <VAlert v-if="serviceErrors !== ''" dense type="error">{{
                    serviceErrors
                  }}</VAlert>
                  <VAlert v-else-if="passwordFeedback" dense :type="messageType">
                    <div
                      v-if="passwordFeedback.suggestions.length !== 0"
                      class="d-flex flex-column"
                      :color="messageType"
                    >
                      <span v-for="suggestion in passwordFeedback.suggestions" :key="suggestion">
                        {{ $t(suggestion) }}
                      </span>
                    </div>
                    <span v-if="passwordFeedback.warning !== ''">{{
                      $t(passwordFeedback.warning)
                    }}</span>
                  </VAlert>
                  <VAlert v-else-if="passwordScore >= MIN_PASSWORD_SCORE" dense type="info">
                    {{ $t('goodPassword.description') }}
                  </VAlert>
                </div>
              </VCol>
            </VRow>
          </VContainer>
        </VCardText>
        <VCardActions>
          <VBtn class="d-none d-sm-flex" color="secondary" text to="/login">
            {{ $t('cancelButton') }}
          </VBtn>
          <VSpacer />
          <VBtn color="primary" :disabled="!canSubmit" type="submit">
            <VIcon left>mdi-shield-key</VIcon>
            {{ $t('setPasswordButton') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VForm>
  </VDialog>
</template>

<style lang="scss" scoped>
/* Overwrite component's default */
.Password {
  max-width: 100%;
}

/* Alert content can have 1, 2 or 3 lines of text; applying a min-height minimises layout shifts */
.alert-wrapper {
  min-height: 82px;
}
</style>
